import { ensureI18NextLanguage } from 'src/configureI18Next';
import i18n from 'i18next';
import React, { useContext, useEffect, useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useNullableSessionUser } from './sessionContext';

interface Value {
    locale: string;
}

export const context = React.createContext<Value | null>(null);

export const LocaleProvider = ({ children }: { children?: React.ReactNode }) => {
    const me = useNullableSessionUser();
    const locale = me?.profile?.locale ?? 'en-CA';
    useEffect(() => {
        let cancelled = false;

        const syncLanguage = async () => {
            if (!i18n.isInitialized || i18n.language === locale) {
                return;
            }

            await ensureI18NextLanguage(locale);
            if (!cancelled && i18n.language !== locale) {
                await i18n.changeLanguage(locale);
            }
        };

        void syncLanguage();

        return () => {
            cancelled = true;
        };
    }, [locale]);

    // useMemo required - context value passed to provider.
    // React Compiler doesn't optimize context values automatically.
    const contextValue = useMemo(() => ({ locale }), [locale]);

    return (
        <I18nextProvider i18n={i18n}>
            <context.Provider value={contextValue}>{children}</context.Provider>
        </I18nextProvider>
    );
};

function useValue() {
    const value = useContext(context);
    if (!value) {
        throw new Error('LocaleProvider is not detected.');
    }
    return value;
}

export function useLocale() {
    const { locale } = useValue();
    return locale;
}
